import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Card,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip
} from '@mui/material';
import { DataGrid, trTR } from '@mui/x-data-grid';
import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  DARK,
  ORANGE,
  darkButtonSx,
  dialogPaperProps,
  lightButtonSx,
  useDynamicStyles
} from 'src/components/dynamicCatalogStyles';
import DynamicListToolbar, { filterDynamicRows } from 'src/components/DynamicListToolbar';

/*
  DIAROLE tablosundaki roller (ROLE_ID sırası önemli).
  Kaydetme prosedürleri (Dia_UpdateUserRole, Dia_UpdateMenuRole, Dia_UpdateComponentRoles)
  "1,0,1,..." listesindeki SIRA numarasını ROLE_ID olarak kaydeder.
*/
export const ROLES = [
  { id: 1, label: 'Çözüm Merkezi Normal' },
  { id: 2, label: 'Çözüm Merkezi Admin' },
  { id: 3, label: 'Değişim Ofis Normal' },
  { id: 4, label: 'Değişim Ofis Admin' },
  { id: 5, label: 'Ürün Değişim Yöneticisi' },
  { id: 6, label: 'Çözüm Merkezi BPO Normal' },
  { id: 7, label: 'Para İadesi Admin' },
  { id: 8, label: 'Satış Yöneticiliği' },
  { id: 9, label: 'Sistem Klima' },
  { id: 10, label: 'Muhasebe' },
  { id: 11, label: 'Tedarik Zinciri' },
  { id: 12, label: 'Kalite Güvence Yöneticiliği' },
  { id: 13, label: 'Muadil Ürün Normal' },
  { id: 14, label: 'Muadil Ürün Admin' }
];

const ROLE_LABELS = ROLES.reduce((acc, r) => ({ ...acc, [r.id]: r.label }), {});

export const parseRoleIds = (roles) =>
  (roles ? roles.toString().split(',') : [])
    .map((x) => Number(String(x).trim()))
    .filter((x) => !isNaN(x) && x > 0);

// Sıra = ROLE_ID olacak şekilde 1..14 için 1/0 listesi
export const buildRoleString = (selectedIds) => {
  const maxId = Math.max(...ROLES.map((r) => r.id));
  const list = [];
  for (let id = 1; id <= maxId; id++) list.push(selectedIds.has(id) ? 1 : 0);
  return list.join(',');
};

const gridLocale = { ...trTR.components.MuiDataGrid.defaultProps.localeText, noRowsLabel: 'Kayıt bulunamadı.' };

// Grid hücresinde rol etiketleri
export function RoleChips({ roles, max = 3, emptyText = 'Rol yok' }) {
  const ids = parseRoleIds(roles);
  if (ids.length === 0) return <Box sx={{ fontSize: '11px', color: '#b0bec5' }}>{emptyText}</Box>;
  const shown = ids.slice(0, max);
  const rest = ids.slice(max);
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', overflow: 'hidden' }}>
      {shown.map((id) => (
        <Box
          key={id}
          sx={{ px: 1, height: 20, lineHeight: '20px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, backgroundColor: '#fff3e0', color: '#e65100', whiteSpace: 'nowrap' }}
        >
          {ROLE_LABELS[id] || `Rol ${id}`}
        </Box>
      ))}
      {rest.length > 0 && (
        <Tooltip title={rest.map((id) => ROLE_LABELS[id] || `Rol ${id}`).join(', ')}>
          <Box sx={{ px: 0.75, height: 20, lineHeight: '20px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, backgroundColor: '#eceff1', color: '#546e7a' }}>
            +{rest.length}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}

// Popup içindeki seçilebilir kutucuk listesi (rol veya işlem)
export function SelectableTiles({ items, selected, original, onToggle }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
      {items.map((it) => {
        const checked = selected.has(it.id);
        const changed = original && original.has(it.id) !== checked;
        return (
          <Box
            key={it.id}
            onClick={() => onToggle(it.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              p: '8px 10px',
              borderRadius: '10px',
              border: `1px solid ${checked ? ORANGE : '#e0e0e0'}`,
              backgroundColor: checked ? '#fff8e1' : '#fff',
              cursor: 'pointer',
              userSelect: 'none',
              transition: 'all 0.15s',
              position: 'relative',
              '&:hover': { borderColor: ORANGE }
            }}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                flexShrink: 0,
                borderRadius: '5px',
                border: `2px solid ${checked ? ORANGE : '#b0bec5'}`,
                backgroundColor: checked ? ORANGE : '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {checked && <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />}
            </Box>
            <Box sx={{ fontSize: '12px', fontWeight: checked ? 700 : 500, color: checked ? DARK : '#546e7a', flex: 1, minWidth: 0 }}>
              {it.label}
            </Box>
            {changed && (
              <Tooltip title={checked ? 'Eklenecek' : 'Kaldırılacak'}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: checked ? '#43a047' : '#e53935', flexShrink: 0 }} />
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

/*
  Rol bazlı yetkilendirme listesi (kullanıcı / menü / işlem).
  - loadAction: liste thunk'ı
  - updateAction: kaydetme thunk'ı, buildPayload(row, roleString) ile çağrılır
*/
function RoleAssignmentPanel({
  title,
  unitLabel,
  loadAction,
  updateAction,
  buildPayload,
  getRowId,
  baseColumns,
  searchFields,
  searchPlaceholder,
  getDialogInfo,
  onSaved
}) {
  const classes = useDynamicStyles();
  const dispatch = useDispatch();
  const [list, setList] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [searchText, setSearchText] = React.useState('');
  const [editRow, setEditRow] = React.useState(null);
  const [selected, setSelected] = React.useState(new Set());
  const [original, setOriginal] = React.useState(new Set());

  const loadList = () => {
    setLoading(true);
    dispatch(loadAction()).then((response) => {
      if (response.meta?.requestStatus === 'fulfilled' && Array.isArray(response.payload)) {
        setList(response.payload);
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const openEdit = (row) => {
    const ids = new Set(parseRoleIds(row.Roles));
    setEditRow(row);
    setSelected(new Set(ids));
    setOriginal(ids);
  };

  const closeEdit = () => {
    if (saving) return;
    setEditRow(null);
  };

  const toggleRole = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const changeCount = ROLES.filter((r) => selected.has(r.id) !== original.has(r.id)).length;

  const save = () => {
    setSaving(true);
    dispatch(updateAction(buildPayload(editRow, buildRoleString(selected)))).then(() => {
      setSaving(false);
      setEditRow(null);
      loadList();
      if (onSaved) onSaved(editRow);
    });
  };

  const filteredRows = useMemo(() => filterDynamicRows(list, { searchText, searchFields }), [list, searchText, searchFields]);

  const columns = [
    ...baseColumns,
    {
      field: 'Roles', headerName: 'Roller', flex: 1.6, minWidth: 260, sortable: false,
      renderCell: (p) => <RoleChips roles={p.value} />
    },
    {
      field: 'actions', headerName: 'İşlem', width: 80, align: 'center', headerAlign: 'center',
      sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <Tooltip title="Rolleri düzenle">
          <IconButton size="small" onClick={() => openEdit(p.row)}>
            <EditIcon sx={{ fontSize: 18, color: ORANGE }} />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  const info = editRow ? getDialogInfo(editRow) : null;

  return (
    <>
      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeader}
          title={title}
          subheader={loading ? 'Yükleniyor…' : `${list.length} ${unitLabel}`}
        />
        <DynamicListToolbar
          searchText={searchText}
          onSearchChange={setSearchText}
          placeholder={searchPlaceholder}
          shownCount={filteredRows.length}
        />
        <DataGrid
          className={classes.grid}
          style={{ height: 560, width: '100%' }}
          columns={columns}
          rows={filteredRows}
          loading={loading}
          rowHeight={44}
          headerHeight={44}
          disableSelectionOnClick
          getRowId={getRowId}
          localeText={gridLocale}
        />
      </Card>

      <Dialog open={!!editRow} onClose={closeEdit} fullWidth maxWidth="sm" PaperProps={dialogPaperProps}>
        <DialogTitle className={classes.dialogTitle}>
          Rolleri Düzenle
          <IconButton size="small" onClick={closeEdit} disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important', opacity: saving ? 0.6 : 1, pointerEvents: saving ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
          {info && (
            <Box sx={{ p: '10px 14px', borderRadius: '12px', border: '1px solid #e0e0e0', mb: 2 }}>
              <Box sx={{ fontSize: '14px', fontWeight: 700, color: '#263238' }}>{info.title}</Box>
              {info.subtitle && <Box sx={{ fontSize: '11px', color: '#78909c' }}>{info.subtitle}</Box>}
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <div className={classes.sectionLabel} style={{ marginBottom: 0 }}>Roller · {selected.size} seçili</div>
            <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
              <Button size="small" sx={{ ...lightButtonSx, fontSize: '11px', minWidth: 0 }} onClick={() => setSelected(new Set(ROLES.map((r) => r.id)))}>
                Tümünü Seç
              </Button>
              <Button size="small" sx={{ ...lightButtonSx, fontSize: '11px', minWidth: 0 }} onClick={() => setSelected(new Set())}>
                Temizle
              </Button>
            </Box>
          </Box>
          <SelectableTiles items={ROLES} selected={selected} original={original} onToggle={toggleRole} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Box sx={{ fontSize: '11px', color: changeCount ? '#e65100' : '#90a4ae', mr: 'auto', pl: 1 }}>
            {changeCount ? `${changeCount} değişiklik` : 'Değişiklik yok'}
          </Box>
          <Button sx={lightButtonSx} onClick={closeEdit} disabled={saving}>Vazgeç</Button>
          <Button
            sx={{ ...darkButtonSx, minWidth: 110 }}
            onClick={save}
            disabled={saving || changeCount === 0}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: ORANGE }} /> : null}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default RoleAssignmentPanel;
